// src/components/Campaigns/ImageUploader.jsx
import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { PhotoCamera, Close } from "@mui/icons-material";
import COLORS from "../../../../../constants/colors";
import { toast } from "react-toastify";

const MAX_FILE_SIZE_MB = 1.5; // Maximum file size in MB
const DESIRED_DIMENSION = 500; // Desired width/height for the square image

const ImageUploader = ({ onImageChange, disabled }) => {
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const processImage = useCallback(
    async (file) => {
      if (!file) {
        setImagePreviewUrl(null);
        onImageChange(null);
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Only image files are allowed.");
        onImageChange(null);
        return;
      }

      setLoading(true);
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Determine the smaller dimension to make it a square
          const size = Math.min(width, height);

          // Calculate the cropping coordinates to center the square
          const sx = (width - size) / 2;
          const sy = (height - size) / 2;

          // Set canvas dimensions to the desired square size
          canvas.width = DESIRED_DIMENSION;
          canvas.height = DESIRED_DIMENSION;

          // Draw the cropped and resized image onto the canvas
          ctx.drawImage(
            img,
            sx,
            sy,
            size,
            size,
            0,
            0,
            DESIRED_DIMENSION,
            DESIRED_DIMENSION
          );

          // Attempt to compress and get a Blob until size is acceptable or quality is too low
          let quality = 0.9; // Start with high quality
          let compressedFile = null;

          const toBlobPromise = (canvas, mimeType, q) =>
            new Promise((resolve) => {
              canvas.toBlob(resolve, mimeType, q);
            });

          const compressLoop = async () => {
            while (true) {
              compressedFile = await toBlobPromise(canvas, "image/jpeg", quality);

              if (
                !compressedFile ||
                compressedFile.size / (1024 * 1024) <= MAX_FILE_SIZE_MB ||
                quality <= 0.1
              ) {
                break;
              }
              quality -= 0.1; // Reduce quality for next iteration
            }
            return compressedFile;
          };

          compressLoop()
            .then((finalBlob) => {
              if (finalBlob) {
                // Convert Blob to File object for consistent handling
                const finalFile = new File([finalBlob], file.name, {
                  type: finalBlob.type,
                  lastModified: Date.now(),
                });
                setImagePreviewUrl(URL.createObjectURL(finalFile));
                onImageChange(finalFile);
                toast.success(
                  `Image processed. Size: ${(
                    finalFile.size /
                    (1024 * 1024)
                  ).toFixed(2)} MB`
                );
              } else {
                toast.error("Failed to process image.");
                setImagePreviewUrl(null);
                onImageChange(null);
              }
            })
            .catch((error) => {
              console.error("Error during image compression:", error);
              toast.error("Error processing image.");
              setImagePreviewUrl(null);
              onImageChange(null);
            })
            .finally(() => {
              setLoading(false);
            });
        };

        img.onerror = () => {
          setLoading(false);
          toast.error("Failed to load image.");
          setImagePreviewUrl(null);
          onImageChange(null);
        };
      };

      reader.onerror = () => {
        setLoading(false);
        toast.error("Failed to read file.");
        setImagePreviewUrl(null);
        onImageChange(null);
      };

      reader.readAsDataURL(file);
    },
    [onImageChange]
  );

  const handleFileChange = useCallback(
    (event) => {
      const selectedFile = event.target.files[0];
      processImage(selectedFile);
    },
    [processImage]
  );

  const handleRemoveImage = useCallback(() => {
    setImagePreviewUrl(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the file input
    }
  }, [onImageChange]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" sx={{ mb: 1, fontWeight: "medium" }}>
        Campaign Image
      </Typography>
      <Box
        sx={{
          border: `2px dashed ${COLORS.border}`,
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 180,
          position: "relative",
          bgcolor: imagePreviewUrl ? "transparent" : "#f9f9f9",
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : imagePreviewUrl ? (
          <>
            <img
              src={imagePreviewUrl}
              alt="Campaign Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "150px",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(255,255,255,0.7)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.9)",
                },
              }}
              onClick={handleRemoveImage}
              disabled={disabled}
            >
              <Close fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <PhotoCamera sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Drag & drop an image here, or click to select
            </Typography>
            <Button
              variant="outlined"
              component="label"
              sx={{
                textTransform: "none",
                borderColor: COLORS.primary,
                color: COLORS.primary,
                "&:hover": {
                  borderColor: COLORS.primary,
                },
              }}
              disabled={disabled}
            >
              Select Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={disabled}
              />
            </Button>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Max size: {MAX_FILE_SIZE_MB}MB. Will be resized to a square.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ImageUploader;